import QuoteItemsEditor from "./QuoteItemsEditor";
import { centsToDisplay, centsToInputValue, dollarsInputToCents } from "@/lib/money";

const BILLING_FREQUENCIES = ["Monthly", "Annual"];
const inputClass = "mt-1 w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm focus:border-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-600/10 dark:border-white/15 dark:bg-white/5 dark:text-white";
const labelClass = "text-xs font-bold uppercase tracking-[0.12em] text-slate-400 dark:text-slate-500";

export default function QuoteEditForm({ form, updateForm, totals, saving, onSave, onReviewAndSend }) {
  return (
    <div className="space-y-6" data-testid="quote-edit-form">
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label className={labelClass}>Company</label>
          <input value={form.company} onChange={(e) => updateForm({ company: e.target.value })} data-testid="quote-field-company" className={inputClass} />
        </div>
        <div>
          <label className={labelClass}>Contact name</label>
          <input value={form.contact_name} onChange={(e) => updateForm({ contact_name: e.target.value })} data-testid="quote-field-contact-name" className={inputClass} />
        </div>
        <div>
          <label className={labelClass}>Contact email</label>
          <input type="email" value={form.contact_email} onChange={(e) => updateForm({ contact_email: e.target.value })} data-testid="quote-field-contact-email" className={inputClass} />
        </div>
        <div>
          <label className={labelClass}>Industry</label>
          <input value={form.industry} onChange={(e) => updateForm({ industry: e.target.value })} data-testid="quote-field-industry" className={inputClass} />
        </div>
        <div>
          <label className={labelClass}>Billing frequency</label>
          <select value={form.billing_frequency} onChange={(e) => updateForm({ billing_frequency: e.target.value })} data-testid="quote-field-billing-frequency" className={inputClass}>
            {BILLING_FREQUENCIES.map((f) => <option key={f} value={f}>{f}</option>)}
          </select>
        </div>
        <div>
          <label className={labelClass}>Valid until</label>
          <input type="date" value={form.valid_until} onChange={(e) => updateForm({ valid_until: e.target.value })} data-testid="quote-field-valid-until" className={inputClass} />
        </div>
        <div>
          <label className={labelClass}>Payment terms</label>
          <input value={form.payment_terms} onChange={(e) => updateForm({ payment_terms: e.target.value })} placeholder="e.g. Due on receipt" data-testid="quote-field-payment-terms" className={inputClass} />
        </div>
      </div>

      <div>
        <h3 className="text-sm font-bold text-slate-950 dark:text-white">Line Items</h3>
        <div className="mt-3">
          <QuoteItemsEditor items={form.items} onChange={(items) => updateForm({ items })} />
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-3">
        <div>
          <label className={labelClass}>Discount</label>
          <select
            value={form.discount.type}
            onChange={(e) => updateForm({ discount: { ...form.discount, type: e.target.value } })}
            data-testid="quote-discount-type"
            className={inputClass}
          >
            <option value="none">No discount</option>
            <option value="percentage">Percentage</option>
            <option value="fixed">Fixed amount</option>
          </select>
        </div>
        {form.discount.type === "percentage" && (
          <div>
            <label className={labelClass}>Percent off</label>
            <input
              type="number"
              min={0}
              max={100}
              value={form.discount.percent || ""}
              onChange={(e) => updateForm({ discount: { ...form.discount, percent: Number(e.target.value) || 0 } })}
              data-testid="quote-discount-percent"
              className={inputClass}
            />
          </div>
        )}
        {form.discount.type === "fixed" && (
          <div>
            <label className={labelClass}>Amount off</label>
            <input
              type="number"
              min={0}
              step="0.01"
              value={centsToInputValue(form.discount.amount_cents)}
              onChange={(e) => updateForm({ discount: { ...form.discount, amount_cents: dollarsInputToCents(e.target.value) } })}
              data-testid="quote-discount-amount"
              className={inputClass}
            />
          </div>
        )}
        {form.discount.type !== "none" && (
          <div>
            <label className={labelClass}>Applies to</label>
            <select
              value={form.discount.applies_to}
              onChange={(e) => updateForm({ discount: { ...form.discount, applies_to: e.target.value } })}
              data-testid="quote-discount-applies-to"
              className={inputClass}
            >
              <option value="both">Recurring &amp; one-time</option>
              <option value="recurring">Recurring only</option>
              <option value="one_time">One-time only</option>
            </select>
          </div>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-4">
        <label className="flex items-center gap-2 text-sm font-semibold text-slate-700 dark:text-slate-300">
          <input type="checkbox" checked={form.tax.enabled} onChange={(e) => updateForm({ tax: { ...form.tax, enabled: e.target.checked } })} data-testid="quote-tax-enabled" />
          Apply tax
        </label>
        {form.tax.enabled && (
          <input
            type="number"
            min={0}
            max={100}
            step="0.01"
            value={form.tax.rate_percent}
            onChange={(e) => updateForm({ tax: { ...form.tax, rate_percent: Number(e.target.value) || 0 } })}
            data-testid="quote-tax-rate"
            className="w-28 rounded-xl border border-slate-300 px-3 py-2 text-sm focus:border-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-600/10 dark:border-white/15 dark:bg-white/5 dark:text-white"
          />
        )}
      </div>

      <div>
        <label className={labelClass}>Notes (shown to customer)</label>
        <textarea value={form.notes || ""} onChange={(e) => updateForm({ notes: e.target.value })} data-testid="quote-field-notes" className={`${inputClass} min-h-20`} />
      </div>
      <div>
        <label className={labelClass}>Terms &amp; Conditions (shown to customer)</label>
        <textarea value={form.terms || ""} onChange={(e) => updateForm({ terms: e.target.value })} data-testid="quote-field-terms" className={`${inputClass} min-h-24`} />
      </div>

      <div className="space-y-1 rounded-xl bg-slate-50 p-5 text-sm dark:bg-white/[0.03]" data-testid="quote-totals-preview">
        <div className="flex justify-between font-bold text-slate-950 dark:text-white">
          <span>Initial total</span>
          <span data-testid="quote-preview-initial-total">{centsToDisplay(totals.initial_total_cents)}</span>
        </div>
        {totals.recurring_total_cents > 0 && (
          <div className="flex justify-between text-slate-500 dark:text-slate-400">
            <span>Then recurring</span>
            <span data-testid="quote-preview-recurring-total">
              {centsToDisplay(totals.recurring_total_cents)} / {form.billing_frequency === "Annual" ? "year" : "month"}
            </span>
          </div>
        )}
      </div>

      <div className="flex flex-wrap gap-3">
        <button
          type="button"
          onClick={onSave}
          disabled={saving}
          data-testid="quote-save-draft-btn"
          className="inline-flex items-center gap-2 rounded-xl border border-slate-300 px-5 py-2.5 text-sm font-bold text-slate-700 transition-colors duration-200 hover:bg-slate-50 disabled:opacity-50 dark:border-white/15 dark:text-slate-200 dark:hover:bg-white/10"
        >
          Save Draft
        </button>
        <button
          type="button"
          onClick={onReviewAndSend}
          disabled={saving}
          data-testid="quote-review-send-btn"
          className="inline-flex items-center gap-2 rounded-xl bg-[#0052FF] px-5 py-2.5 text-sm font-bold text-white transition-colors duration-200 hover:bg-blue-700 disabled:opacity-50"
        >
          Review &amp; Send
        </button>
      </div>
    </div>
  );
}
